import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, TrendingUp, ArrowRight, ImageOff } from 'lucide-react';
import { format } from 'date-fns';
import { getArticleImage, handleImageError } from '../utils/imageUtils';

/**
 * ArticleCard - Premium editorial article cards with robust image handling
 * Variants: default, compact, large, hero, featured
 * 
 * Uses centralized getArticleImage() for consistent image handling.
 */
export const ArticleCard = ({ article, variant = 'default', hero = false, featured = false }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const imageUrl = getArticleImage(article);

  if (hero) variant = 'hero';
  if (featured && variant === 'default') variant = 'featured';

  const CardImage = ({ className = '', eager = false, aspectRatio = 'aspect-[16/9]' }) => (
    <div className={`relative overflow-hidden bg-gray-100 ${aspectRatio}`}>
      {!imageLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
      <img
        src={imageUrl}
        alt={article.featured_image_alt || article.title}
        className={`w-full h-full object-cover ${className} ${imageLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
        loading={eager ? 'eager' : 'lazy'}
        onLoad={() => setImageLoaded(true)}
        onError={(e) => {
          handleImageError(e);
          setImageLoaded(true);
        }}
      />
    </div>
  );

  // Hero variant
  if (variant === 'hero') {
    return (
      <Link to={`/article/${article.slug}`} className="group block relative h-full" data-testid={`article-card-${article.slug}`}>
        <div className="relative overflow-hidden rounded-2xl bg-gray-900 h-full min-h-[400px]">
          <div className="absolute inset-0">
            <img
              src={imageUrl}
              alt={article.featured_image_alt || article.title}
              className="w-full h-full object-cover group-hover:scale-105 smooth-transition duration-700 opacity-80"
              loading="eager"
              onError={handleImageError}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/95 via-gray-900/60 to-gray-900/20"></div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 text-white">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-brand-red text-white text-xs font-bold uppercase tracking-wider rounded-full" data-testid="category-name">
                {article.category_name || 'Article'}
              </span>
              {article.is_featured && (
                <span className="px-3 py-1 bg-brand-yellow text-gray-900 text-xs font-bold uppercase tracking-wider rounded-full flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  Featured
                </span>
              )}
            </div>
            <h2 className="text-3xl md:text-5xl font-playfair font-bold mb-4 leading-tight drop-shadow-lg group-hover:text-brand-yellow smooth-transition" data-testid="article-title">
              {article.title}
            </h2>
            {article.excerpt && (
              <p className="text-base md:text-lg text-gray-100 mb-4 line-clamp-2 leading-relaxed max-w-3xl" data-testid="article-excerpt">
                {article.excerpt}
              </p>
            )}
            <div className="flex items-center gap-4 text-sm text-gray-200">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {format(new Date(article.published_at || article.created_at), 'MMM dd, yyyy')}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {article.reading_time || 5} min read
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Large variant
  if (variant === 'large') {
    return (
      <Link to={`/article/${article.slug}`} className="group block h-full" data-testid={`article-card-${article.slug}`}>
        <div className="bg-white rounded-xl overflow-hidden h-full flex flex-col hover:shadow-xl smooth-transition border border-gray-100">
          <div className="relative">
            <CardImage className="group-hover:scale-105 smooth-transition duration-500" aspectRatio="aspect-[16/9]" />
            <div className="absolute top-4 left-4">
              <span className="px-3 py-1.5 bg-brand-red text-white text-xs font-bold uppercase tracking-wider rounded-lg shadow-lg">
                {article.category_name || 'Article'}
              </span>
            </div>
          </div>
          <div className="p-6 flex-1 flex flex-col">
            <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-3 group-hover:text-brand-red smooth-transition leading-tight line-clamp-3" data-testid="article-title">
              {article.title}
            </h3>
            {article.excerpt && (
              <p className="text-gray-600 leading-relaxed mb-4 line-clamp-3 flex-1" data-testid="article-excerpt">
                {article.excerpt}
              </p>
            )}
            <div className="flex items-center justify-between mt-auto">
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {format(new Date(article.published_at || article.created_at), 'MMM dd')}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {article.reading_time || 5} min
                </span>
              </div>
              <span className="text-brand-red font-semibold text-sm flex items-center gap-1 group-hover:gap-2 smooth-transition">
                Read <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Compact variant
  if (variant === 'compact') {
    return (
      <Link to={`/article/${article.slug}`} className="group block" data-testid={`article-card-${article.slug}`}>
        <div className="bg-white rounded-xl overflow-hidden hover:shadow-lg smooth-transition border border-gray-100 flex h-28">
          <div className="w-28 h-28 flex-shrink-0 relative overflow-hidden bg-gray-100">
            <img
              src={imageUrl}
              alt={article.featured_image_alt || article.title}
              className="w-full h-full object-cover group-hover:scale-105 smooth-transition duration-500"
              loading="lazy"
              onError={handleImageError}
            />
          </div>
          <div className="flex-1 p-4 flex flex-col justify-center min-w-0">
            <span className="text-xs text-brand-red font-semibold uppercase tracking-wide mb-1">
              {article.category_name || 'Article'}
            </span>
            <h3 className="font-playfair font-bold text-gray-900 mb-1 line-clamp-2 group-hover:text-brand-red smooth-transition leading-snug text-sm" data-testid="article-title">
              {article.title}
            </h3>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              {article.reading_time || 5} min read
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Featured variant
  if (variant === 'featured') {
    return (
      <Link to={`/article/${article.slug}`} className="group block" data-testid={`article-card-${article.slug}`}>
        <div className="bg-white rounded-xl overflow-hidden hover:shadow-xl smooth-transition border border-gray-100">
          <div className="relative">
            <CardImage className="group-hover:scale-105 smooth-transition duration-500" aspectRatio="aspect-[16/9]" />
            <div className="absolute top-4 left-4">
              <span className="px-3 py-1.5 bg-brand-red text-white text-xs font-bold uppercase tracking-wider rounded-lg shadow-lg">
                {article.category_name || 'Article'}
              </span>
            </div>
          </div>
          <div className="p-6">
            <h3 className="text-xl font-playfair font-bold text-gray-900 mb-3 group-hover:text-brand-red smooth-transition leading-tight line-clamp-2" data-testid="article-title">
              {article.title}
            </h3>
            {article.excerpt && (
              <p className="text-gray-600 leading-relaxed mb-4 line-clamp-2" data-testid="article-excerpt">
                {article.excerpt}
              </p>
            )}
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {format(new Date(article.published_at || article.created_at), 'MMM dd')}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {article.reading_time || 5} min
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Default variant
  return (
    <Link to={`/article/${article.slug}`} className="group block h-full" data-testid={`article-card-${article.slug}`}>
      <div className="bg-white rounded-xl overflow-hidden hover:shadow-lg smooth-transition border border-gray-100 h-full flex flex-col">
        <div className="relative flex-shrink-0">
          <CardImage className="group-hover:scale-105 smooth-transition duration-500" aspectRatio="aspect-[16/9]" />
          {article.is_sponsored && (
            <div className="absolute top-3 right-3">
              <span className="px-2 py-1 bg-gray-900/80 text-white text-xs font-medium rounded">
                Sponsored
              </span>
            </div>
          )}
        </div>
        <div className="p-5 flex-1 flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-brand-red font-semibold uppercase tracking-wider" data-testid="category-name">
              {article.category_name || 'Article'}
            </span>
            <span className="text-gray-300">•</span>
            <span className="text-xs text-gray-500">
              {format(new Date(article.published_at || article.created_at), 'MMM dd')}
            </span>
          </div>
          <h3 className="text-lg font-playfair font-bold text-gray-900 mb-2 group-hover:text-brand-red smooth-transition leading-tight line-clamp-2" data-testid="article-title">
            {article.title}
          </h3>
          {article.excerpt && (
            <p className="text-sm text-gray-600 line-clamp-2 mb-3 leading-relaxed flex-1" data-testid="article-excerpt">
              {article.excerpt}
            </p>
          )}
          <div className="flex items-center justify-between mt-auto">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Clock className="w-3.5 h-3.5" />
              {article.reading_time || 5} min read
            </div>
            <span className="text-brand-red font-medium text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 smooth-transition">
              Read More <ArrowRight className="w-3 h-3" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ArticleCard;
